import React from 'react'
import { Helmet } from 'react-helmet-async'
import { FaArrowRight, FaCheck, FaLayerGroup, FaShieldAlt } from 'react-icons/fa'
import { MdOutlineInsights } from 'react-icons/md'
import Header from './Header'
import Preloader from './Preloader'
import ScrollReveal from './ScrollReveal'
import Footer from './Footer'

const TechnologyServicePage = ({
    badge,
    title,
    description,
    metaDescription,
    icon,
    capabilities,
    outcomes,
    highlights,
    cta,
    featuredVisual
}) => {
    return (
        <div>
            <Helmet>
                <title>{badge} | VRIFY Technologies</title>
                <meta name="description" content={metaDescription || description} />
            </Helmet>

            <div className="font-nanum-myeongjo min-h-screen overflow-hidden bg-slate-950 pt-16 text-slate-100 md:pt-20 lg:pt-16">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.2),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.13),_transparent_22%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <main>
                        <section className="mx-auto max-w-7xl px-4 pb-10 pt-8 sm:px-6 lg:px-8 lg:pb-16">
                            <ScrollReveal>
                                <div className="grid items-center gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
                                    <div>
                                        <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-yellow-200">
                                            {icon}
                                            {badge}
                                        </p>
                                        <h1 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                                            {title}
                                        </h1>
                                        <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                            {description}
                                        </p>

                                        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                            <a
                                                href="/contactus"
                                                className="inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                            >
                                                Talk to us
                                                <FaArrowRight className="text-xs" />
                                            </a>
                                            <a
                                                href="#capabilities"
                                                className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-3 text-sm font-semibold text-white transition hover:border-yellow-300 hover:text-yellow-200"
                                            >
                                                View capabilities
                                            </a>
                                        </div>
                                    </div>

                                    <div className="rounded-[28px] border border-white/10 bg-slate-900/85 p-5 shadow-2xl shadow-black/30 backdrop-blur sm:p-6">
                                        <div className="rounded-[24px] bg-gradient-to-br from-yellow-400 via-amber-300 to-sky-100 p-[1px]">
                                            <div className="rounded-[23px] bg-slate-950/95 p-5 sm:p-6">
                                                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-yellow-300">
                                                    Outcomes
                                                </p>
                                                <div className="mt-5 grid gap-4">
                                                    {outcomes.map((item) => (
                                                        <div key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                                            <div className="flex items-start gap-3">
                                                                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-yellow-400 text-slate-950">
                                                                    <FaCheck className="text-sm" />
                                                                </span>
                                                                <div>
                                                                    <h2 className="text-sm font-semibold text-white">{item.title}</h2>
                                                                    <p className="mt-1 text-sm leading-6 text-slate-300">{item.description}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </ScrollReveal>
                        </section>

                        {featuredVisual && (
                            <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8 lg:pb-14">
                                <ScrollReveal>
                                    <div className="overflow-hidden rounded-[30px] border border-white/10 bg-slate-900/80 shadow-2xl shadow-black/25 backdrop-blur-sm">
                                        <div className="grid items-stretch lg:grid-cols-[0.72fr_1.28fr]">
                                            <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10 xl:p-12">
                                                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-yellow-300">
                                                    {featuredVisual.eyebrow}
                                                </p>
                                                <h2 className="mt-3 text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">
                                                    {featuredVisual.title}
                                                </h2>
                                                <p className="mt-4 max-w-xl text-sm leading-7 text-slate-300 sm:text-base">
                                                    {featuredVisual.description}
                                                </p>

                                                {featuredVisual.points?.length > 0 && (
                                                    <div className="mt-6 flex flex-wrap gap-2">
                                                        {featuredVisual.points.map((point) => (
                                                            <span
                                                                key={point}
                                                                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold leading-5 text-slate-200"
                                                            >
                                                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-300" />
                                                                {point}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <figure className="relative overflow-hidden border-t border-white/10 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-3 sm:p-5 lg:border-l lg:border-t-0 lg:p-6">
                                                <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-sky-300/20 blur-3xl" />
                                                <div className="pointer-events-none absolute -bottom-16 -left-16 h-52 w-52 rounded-full bg-emerald-300/20 blur-3xl" />
                                                <div className="relative mx-auto aspect-square w-full max-w-[720px] overflow-hidden rounded-[22px] bg-white shadow-[0_20px_60px_rgba(15,23,42,0.12)] ring-1 ring-slate-900/5">
                                                    <img
                                                        src={featuredVisual.image}
                                                        alt={featuredVisual.alt}
                                                        className="h-full w-full object-contain"
                                                        loading="eager"
                                                        decoding="async"
                                                    />
                                                </div>
                                            </figure>
                                        </div>
                                    </div>
                                </ScrollReveal>
                            </section>
                        )}

                        <section id="capabilities" className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                            <ScrollReveal>
                                <div className="max-w-3xl text-center sm:text-left">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-yellow-300">
                                        What we deliver
                                    </p>
                                    <h2 className="mt-3 text-2xl font-bold text-white sm:text-3xl">
                                        Focused capabilities from the technology services portfolio.
                                    </h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-300 sm:text-base">
                                        Clear, practical services designed for secure, scalable, and intelligent digital operations.
                                    </p>
                                </div>
                            </ScrollReveal>

                            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {capabilities.map((item) => (
                                    <ScrollReveal key={item.title}>
                                        <article className="flex h-full gap-4 rounded-[24px] border border-white/10 bg-white/5 p-5 shadow-lg shadow-black/10 transition hover:-translate-y-1 hover:border-yellow-300/60">
                                            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-yellow-300">
                                                <FaLayerGroup className="text-base" />
                                            </span>
                                            <div>
                                                <h3 className="text-base font-semibold text-white">{item.title}</h3>
                                                <p className="mt-2 text-sm leading-6 text-slate-300">{item.description}</p>
                                            </div>
                                        </article>
                                    </ScrollReveal>
                                ))}
                            </div>
                        </section>

                        <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                            <div className="grid gap-4 md:grid-cols-3">
                                {highlights.map((item, index) => (
                                    <ScrollReveal key={item.title}>
                                        <div className="h-full rounded-[24px] border border-white/10 bg-slate-900/75 p-5">
                                            <div className="flex items-center justify-between">
                                                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-yellow-300">
                                                    0{index + 1}
                                                </p>
                                                {index === 1 ? (
                                                    <FaShieldAlt className="text-lg text-yellow-300" />
                                                ) : (
                                                    <MdOutlineInsights className="text-xl text-yellow-300" />
                                                )}
                                            </div>
                                            <h3 className="mt-4 text-lg font-semibold text-white">{item.title}</h3>
                                            <p className="mt-2 text-sm leading-7 text-slate-300">{item.description}</p>
                                        </div>
                                    </ScrollReveal>
                                ))}
                            </div>
                        </section>

                        <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                            <ScrollReveal>
                                <div className="rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10">
                                    <div className="max-w-2xl">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-950">
                                            {cta.eyebrow}
                                        </p>
                                        <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">{cta.title}</h2>
                                        <p className="mt-3 text-sm leading-7 text-slate-900/80 sm:text-base">{cta.description}</p>
                                    </div>
                                    <a
                                        href="/contactus"
                                        className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-200 transition hover:bg-slate-900 lg:mt-0"
                                    >
                                        Start a conversation
                                    </a>
                                </div>
                            </ScrollReveal>
                        </section>
                    </main>

                    <Footer />
                </div>
            </div>
        </div>
    )
}

export default TechnologyServicePage
